import { useState } from 'react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { toast } from 'sonner';
import { downloadMethodologyAsMarkdown } from '@/lib/flexeMethodologyDocument';

interface ChartConfig {
  id: string;
  name: string;
  filename: string;
}

const CHART_CONFIGS: ChartConfig[] = [
  { id: 'flexe-chart-summary', name: 'Samenvatting & KPIs', filename: 'samenvatting_kpis' },
  { id: 'flexe-chart-baseline-scenario', name: 'Baseline vs. Scenario', filename: 'baseline_vs_scenario' },
  { id: 'flexe-chart-energy-deficit', name: 'Energiebehoefte', filename: 'energiebehoefte_vs_capaciteit' },
  { id: 'flexe-chart-power-deficit', name: 'Vermogensoverschrijding', filename: 'vermogen_vs_batterij' },
  { id: 'flexe-chart-scatter', name: 'Scatter Plot', filename: 'dagresultaten_scatter' },
];

export function useFlexEExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);

  const captureChart = async (chartId: string): Promise<Blob | null> => {
    const element = document.getElementById(chartId);
    if (!element) {
      console.warn(`Chart element not found: ${chartId}`);
      return null;
    }

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
      });
    } catch (error) {
      console.error(`Error capturing chart ${chartId}:`, error);
      return null;
    }
  };

  const downloadSingleChart = async (chartId: string, filename: string) => {
    setIsExporting(true);
    setExportProgress('Grafiek exporteren...');

    try {
      const blob = await captureChart(chartId);
      if (!blob) {
        toast.error('Grafiek niet gevonden');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Grafiek gedownload');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export mislukt');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const downloadAllChartsAsZip = async (simulationName: string) => {
    setIsExporting(true);
    const zip = new JSZip();
    let successCount = 0;

    try {
      for (let i = 0; i < CHART_CONFIGS.length; i++) {
        const config = CHART_CONFIGS[i];
        setExportProgress(`Exporteren: ${config.name} (${i + 1}/${CHART_CONFIGS.length})`);

        const blob = await captureChart(config.id);
        if (blob) {
          zip.file(`${config.filename}.png`, blob);
          successCount++;
        }
      }

      if (successCount === 0) {
        toast.error('Geen grafieken gevonden om te exporteren');
        return;
      }

      setExportProgress('ZIP bestand genereren...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      const safeName = simulationName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `flexe_${safeName}_grafieken.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`${successCount} grafieken gedownload als ZIP`);
    } catch (error) {
      console.error('ZIP export error:', error);
      toast.error('ZIP export mislukt');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const downloadMethodology = (simulationData?: {
    contractLimit?: number;
    battKw?: number;
    battKwh?: number;
    windowStart?: string;
    windowEnd?: string;
    successRate?: number;
    baselineSuccessRate?: number;
  }) => {
    try {
      downloadMethodologyAsMarkdown(simulationData);
      toast.success('Methodiekdocument gedownload');
    } catch (error) {
      console.error('Methodology download error:', error);
      toast.error('Download mislukt');
    }
  };

  return {
    isExporting,
    exportProgress,
    downloadSingleChart,
    downloadAllChartsAsZip,
    downloadMethodology,
    chartConfigs: CHART_CONFIGS,
  };
}
